/** Model-driven Power Apps lifecycle tools backed by the Dataverse Web API. */

import { z } from "zod";
import type { Tool } from "@modelcontextprotocol/sdk/types.js";
import type {
  ModelDrivenApi,
  ModelDrivenApp,
  ModelDrivenAppComponent,
  ModelDrivenAppValidationIssue,
} from "../api/model-driven-api.js";
import { logger } from "../utils/logger.js";
import { ConfirmationRequiredError } from "../utils/errors.js";

const APP_ID_DESCRIPTION = "The model-driven app's AppModule ID (GUID)";
const ROLE_ID_DESCRIPTION = "The Dataverse security role ID (GUID)";

const componentProperties = {
  entityType: {
    type: "string",
    description:
      "Dataverse entity logical name, for example savedquery, systemform, workflow, or sitemap",
  },
  id: { type: "string", description: "Component record ID (GUID)" },
  idProperty: {
    type: "string",
    description:
      "Primary ID property override. Usually inferred; use formid for systemform (already inferred)",
  },
} as const;

export const listModelDrivenAppsTool: Tool = {
  name: "list_model_driven_apps",
  description:
    "List published or unpublished model-driven Power Apps from Dataverse, including AppModule IDs and unique names.",
  inputSchema: {
    type: "object",
    properties: {
      unpublished: {
        type: "boolean",
        description: "Return the unpublished customization layer instead of published apps",
      },
      top: { type: "number", minimum: 1, maximum: 250, description: "Maximum apps to return" },
    },
  },
};

export const getModelDrivenAppTool: Tool = {
  name: "get_model_driven_app",
  description:
    "Get a model-driven app's published or unpublished AppModule record. Optionally includes the complete descriptor/app graph/config XML definition.",
  inputSchema: {
    type: "object",
    properties: {
      appId: { type: "string", description: APP_ID_DESCRIPTION },
      unpublished: { type: "boolean", description: "Read the unpublished customization layer" },
      includeDefinition: {
        type: "boolean",
        description: "Include descriptor, appgraph, configxml, and appmodulexmlmanaged fields",
      },
    },
    required: ["appId"],
  },
};

export const createModelDrivenAppTool: Tool = {
  name: "create_model_driven_app",
  description:
    "Create a real model-driven AppModule in Dataverse. This creates the app shell; add components, validate, publish, and grant security roles with the companion tools.",
  inputSchema: {
    type: "object",
    properties: {
      name: { type: "string", minLength: 1, maxLength: 100, description: "App display name" },
      uniqueName: {
        type: "string",
        minLength: 1,
        maxLength: 100,
        pattern: "^[A-Za-z][A-Za-z0-9_]*$",
        description: "Unique app name; the solution publisher prefix is applied by Dataverse",
      },
      description: { type: "string", maxLength: 2000 },
      webResourceId: {
        type: "string",
        description:
          "Icon web-resource GUID; uses Microsoft's documented default icon when omitted",
      },
      solutionUniqueName: {
        type: "string",
        description: "Unmanaged solution unique name to receive the new app",
      },
    },
    required: ["name", "uniqueName"],
  },
};

export const updateModelDrivenAppTool: Tool = {
  name: "update_model_driven_app",
  description:
    "Update supported properties on a published model-driven AppModule, creating unpublished changes. A newly-created app must be published once before its properties can be updated through the documented AppModule API.",
  inputSchema: {
    type: "object",
    properties: {
      appId: { type: "string", description: APP_ID_DESCRIPTION },
      name: { type: "string", minLength: 1, maxLength: 100 },
      description: {
        anyOf: [{ type: "string", maxLength: 2000 }, { type: "null" }],
        description: "New description, or null to clear it",
      },
      url: {
        anyOf: [{ type: "string", maxLength: 200 }, { type: "null" }],
        description: "App URL value, or null to clear it",
      },
      appModuleVersion: { type: "string", maxLength: 48 },
      isFeatured: { type: "boolean" },
      navigationType: {
        type: "integer",
        enum: [0, 1],
        description: "0 for single-session navigation or 1 for multi-session navigation",
      },
      formFactor: { type: "integer", minimum: 1, maximum: 8 },
      solutionUniqueName: {
        type: "string",
        description: "Unmanaged solution context for the customization",
      },
    },
    required: ["appId"],
  },
};

export const deleteModelDrivenAppTool: Tool = {
  name: "delete_model_driven_app",
  description: "Permanently delete a model-driven AppModule. Requires confirm=true.",
  inputSchema: {
    type: "object",
    properties: {
      appId: { type: "string", description: APP_ID_DESCRIPTION },
      confirm: { type: "boolean", description: "Set true to confirm permanent deletion" },
    },
    required: ["appId"],
  },
};

export const getModelDrivenAppComponentsTool: Tool = {
  name: "get_model_driven_app_components",
  description: "Retrieve all components currently included in a published model-driven app.",
  inputSchema: {
    type: "object",
    properties: { appId: { type: "string", description: APP_ID_DESCRIPTION } },
    required: ["appId"],
  },
};

export const addModelDrivenAppComponentsTool: Tool = {
  name: "add_model_driven_app_components",
  description:
    "Add Dataverse components such as views, forms, workflows, dashboards, and sitemaps to a model-driven app through AddAppComponents.",
  inputSchema: {
    type: "object",
    properties: {
      appId: { type: "string", description: APP_ID_DESCRIPTION },
      components: {
        type: "array",
        minItems: 1,
        maxItems: 100,
        items: {
          type: "object",
          properties: componentProperties,
          required: ["entityType", "id"],
          additionalProperties: false,
        },
      },
    },
    required: ["appId", "components"],
  },
};

export const removeModelDrivenAppComponentsTool: Tool = {
  name: "remove_model_driven_app_components",
  description:
    "Remove one or more Dataverse components from a model-driven app through RemoveAppComponents.",
  inputSchema: {
    type: "object",
    properties: {
      appId: { type: "string", description: APP_ID_DESCRIPTION },
      components: {
        type: "array",
        minItems: 1,
        maxItems: 100,
        items: {
          type: "object",
          properties: componentProperties,
          required: ["entityType", "id"],
          additionalProperties: false,
        },
      },
    },
    required: ["appId", "components"],
  },
};

export const validateModelDrivenAppTool: Tool = {
  name: "validate_model_driven_app",
  description:
    "Run Dataverse ValidateApp and return dependency errors and warnings before publishing.",
  inputSchema: {
    type: "object",
    properties: { appId: { type: "string", description: APP_ID_DESCRIPTION } },
    required: ["appId"],
  },
};

export const publishModelDrivenAppTool: Tool = {
  name: "publish_model_driven_app",
  description:
    "Validate and publish one model-driven app via PublishXml. Validation blocks publishing unless skipValidation=true.",
  inputSchema: {
    type: "object",
    properties: {
      appId: { type: "string", description: APP_ID_DESCRIPTION },
      skipValidation: {
        type: "boolean",
        description: "Publish without calling ValidateApp first (default false)",
      },
    },
    required: ["appId"],
  },
};

export const listModelDrivenAppRolesTool: Tool = {
  name: "list_model_driven_app_roles",
  description: "List Dataverse security roles associated with a model-driven app.",
  inputSchema: {
    type: "object",
    properties: { appId: { type: "string", description: APP_ID_DESCRIPTION } },
    required: ["appId"],
  },
};

export const grantModelDrivenAppRoleTool: Tool = {
  name: "grant_model_driven_app_role",
  description: "Associate a Dataverse security role with a model-driven app to grant access.",
  inputSchema: {
    type: "object",
    properties: {
      appId: { type: "string", description: APP_ID_DESCRIPTION },
      roleId: { type: "string", description: ROLE_ID_DESCRIPTION },
    },
    required: ["appId", "roleId"],
  },
};

export const revokeModelDrivenAppRoleTool: Tool = {
  name: "revoke_model_driven_app_role",
  description: "Disassociate a Dataverse security role from a model-driven app.",
  inputSchema: {
    type: "object",
    properties: {
      appId: { type: "string", description: APP_ID_DESCRIPTION },
      roleId: { type: "string", description: ROLE_ID_DESCRIPTION },
    },
    required: ["appId", "roleId"],
  },
};

const guid = z
  .string()
  .regex(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i, "Expected a GUID");
const uniqueName = z.string().regex(/^[A-Za-z][A-Za-z0-9_]{0,99}$/);
const appIdSchema = z.object({ appId: guid });
const listSchema = z.object({
  unpublished: z.boolean().optional().default(false),
  top: z.number().int().min(1).max(250).optional(),
});
const getSchema = z.object({
  appId: guid,
  unpublished: z.boolean().optional().default(false),
  includeDefinition: z.boolean().optional().default(false),
});
const createSchema = z.object({
  name: z.string().trim().min(1).max(100),
  uniqueName,
  description: z.string().max(2000).optional(),
  webResourceId: guid.optional(),
  solutionUniqueName: uniqueName.optional(),
});
const updateSchema = z
  .object({
    appId: guid,
    name: z.string().trim().min(1).max(100).optional(),
    description: z.string().max(2000).nullable().optional(),
    url: z.string().max(200).nullable().optional(),
    appModuleVersion: z.string().min(1).max(48).optional(),
    isFeatured: z.boolean().optional(),
    navigationType: z.union([z.literal(0), z.literal(1)]).optional(),
    formFactor: z.number().int().min(1).max(8).optional(),
    solutionUniqueName: uniqueName.optional(),
  })
  .refine(
    (value) =>
      value.name !== undefined ||
      value.description !== undefined ||
      value.url !== undefined ||
      value.appModuleVersion !== undefined ||
      value.isFeatured !== undefined ||
      value.navigationType !== undefined ||
      value.formFactor !== undefined,
    { message: "Provide at least one model-driven app property to update" },
  );
const deleteSchema = z.object({ appId: guid, confirm: z.boolean().optional().default(false) });
const componentSchema = z.object({
  entityType: z.string().regex(/^[A-Za-z][A-Za-z0-9_]{0,127}$/),
  id: guid,
  idProperty: z
    .string()
    .regex(/^[A-Za-z][A-Za-z0-9_]{0,127}$/)
    .optional(),
});
const componentsSchema = z.object({
  appId: guid,
  components: z.array(componentSchema).min(1).max(100),
});
const publishSchema = z.object({
  appId: guid,
  skipValidation: z.boolean().optional().default(false),
});
const roleSchema = z.object({ appId: guid, roleId: guid });

function formatApp(app: ModelDrivenApp, includeDefinition: boolean): string {
  const lines = [
    `Name: ${app.name}`,
    `ID: ${app.appmoduleid}`,
    `Unique name: ${app.uniquename}`,
    `Version: ${app.appmoduleversion ?? "unknown"}`,
    `State: ${app.componentstate ?? "unknown"}`,
    `Managed: ${app.ismanaged ?? "unknown"}`,
  ];
  if (app.description) lines.push(`Description: ${app.description}`);
  if (app.url) lines.push(`URL: ${app.url}`);
  if (app.modifiedon) lines.push(`Modified: ${app.modifiedon}`);
  if (app.publishedon) lines.push(`Published: ${app.publishedon}`);
  if (includeDefinition) {
    lines.push("Definition:");
    lines.push(
      JSON.stringify(
        {
          descriptor: app.descriptor,
          appgraph: app.appgraph,
          configxml: app.configxml,
          appmodulexmlmanaged: app.appmodulexmlmanaged,
        },
        null,
        2,
      ),
    );
  }
  return lines.join("\n");
}

function formatValidationIssue(issue: ModelDrivenAppValidationIssue): string {
  const message = issue.Message ?? issue.Description ?? "Validation issue";
  const component = issue.ComponentName ?? issue.ComponentId;
  const type = issue.ErrorType !== undefined ? ` [type ${String(issue.ErrorType)}]` : "";
  return `  - ${message}${component ? ` (${component})` : ""}${type}`;
}

function componentSummary(component: ModelDrivenAppComponent): string {
  const type =
    typeof component["@odata.type"] === "string"
      ? component["@odata.type"].replace("#Microsoft.Dynamics.CRM.", "")
      : "component";
  const idEntry = Object.entries(component).find(
    ([key, value]) => key.endsWith("id") && typeof value === "string",
  );
  return `  - ${type}${idEntry ? ` ${String(idEntry[1])}` : ""}: ${JSON.stringify(component)}`;
}

export async function handleListModelDrivenApps(
  api: ModelDrivenApi,
  input: unknown,
): Promise<string> {
  const parsed = listSchema.parse(input);
  logger.info(parsed, "Executing list_model_driven_apps");
  const apps = await api.listApps({ unpublished: parsed.unpublished, top: parsed.top });
  if (apps.length === 0) {
    return `No ${parsed.unpublished ? "unpublished " : ""}model-driven apps found.`;
  }
  return [
    `${parsed.unpublished ? "Unpublished m" : "M"}odel-driven apps (${apps.length}):`,
    ...apps.map(
      (app) =>
        `  ${app.name} (${app.appmoduleid}) - ${app.uniquename}${app.appmoduleversion ? ` - v${app.appmoduleversion}` : ""}`,
    ),
  ].join("\n");
}

export async function handleGetModelDrivenApp(
  api: ModelDrivenApi,
  input: unknown,
): Promise<string> {
  const parsed = getSchema.parse(input);
  const app = await api.getApp(parsed.appId, {
    unpublished: parsed.unpublished,
    includeDefinition: parsed.includeDefinition,
  });
  return formatApp(app, parsed.includeDefinition);
}

export async function handleCreateModelDrivenApp(
  api: ModelDrivenApi,
  input: unknown,
): Promise<string> {
  const parsed = createSchema.parse(input);
  const result = await api.createApp(parsed);
  return [
    `Created model-driven app "${parsed.name}" (${result.appId}).`,
    "Next: add components, validate the app, publish it, and grant one or more security roles.",
  ].join("\n");
}

export async function handleUpdateModelDrivenApp(
  api: ModelDrivenApi,
  input: unknown,
): Promise<string> {
  const parsed = updateSchema.parse(input);
  const { appId, ...options } = parsed;
  await api.updateApp(appId, options);
  return `Updated model-driven app ${appId}. Publish it to make the changes available.`;
}

export async function handleDeleteModelDrivenApp(
  api: ModelDrivenApi,
  input: unknown,
): Promise<string> {
  const parsed = deleteSchema.parse(input);
  if (!parsed.confirm) {
    throw new ConfirmationRequiredError(`Confirm permanent deletion of model-driven app ${parsed.appId}? Set confirm=true to proceed.`);
  }
  await api.deleteApp(parsed.appId);
  return `Deleted model-driven app ${parsed.appId}.`;
}

export async function handleGetModelDrivenAppComponents(
  api: ModelDrivenApi,
  input: unknown,
): Promise<string> {
  const { appId } = appIdSchema.parse(input);
  const components = await api.retrieveComponents(appId);
  if (components.length === 0) return `No components found for model-driven app ${appId}.`;
  return [
    `Components for model-driven app ${appId} (${components.length}):`,
    ...components.map(componentSummary),
  ].join("\n");
}

export async function handleAddModelDrivenAppComponents(
  api: ModelDrivenApi,
  input: unknown,
): Promise<string> {
  const parsed = componentsSchema.parse(input);
  await api.addComponents(parsed.appId, parsed.components);
  return `Added ${parsed.components.length} component(s) to model-driven app ${parsed.appId}.`;
}

export async function handleRemoveModelDrivenAppComponents(
  api: ModelDrivenApi,
  input: unknown,
): Promise<string> {
  const parsed = componentsSchema.parse(input);
  await api.removeComponents(parsed.appId, parsed.components);
  return `Removed ${parsed.components.length} component(s) from model-driven app ${parsed.appId}.`;
}

export async function handleValidateModelDrivenApp(
  api: ModelDrivenApi,
  input: unknown,
): Promise<string> {
  const { appId } = appIdSchema.parse(input);
  const validation = await api.validateApp(appId);
  if (validation.ValidationSuccess && validation.ValidationIssueList.length === 0) {
    return `Model-driven app ${appId} passed validation with no issues.`;
  }
  const heading = validation.ValidationSuccess
    ? `Model-driven app ${appId} passed validation with ${validation.ValidationIssueList.length} warning(s):`
    : `Model-driven app ${appId} failed validation with ${validation.ValidationIssueList.length} issue(s):`;
  return [heading, ...validation.ValidationIssueList.map(formatValidationIssue)].join("\n");
}

export async function handlePublishModelDrivenApp(
  api: ModelDrivenApi,
  input: unknown,
): Promise<string> {
  const parsed = publishSchema.parse(input);
  if (!parsed.skipValidation) {
    const validation = await api.validateApp(parsed.appId);
    if (!validation.ValidationSuccess) {
      return [
        `Not published: model-driven app ${parsed.appId} failed validation.`,
        ...validation.ValidationIssueList.map(formatValidationIssue),
      ].join("\n");
    }
  }
  await api.publishApp(parsed.appId);
  return `Published model-driven app ${parsed.appId}.`;
}

export async function handleListModelDrivenAppRoles(
  api: ModelDrivenApi,
  input: unknown,
): Promise<string> {
  const { appId } = appIdSchema.parse(input);
  const roles = await api.listSecurityRoles(appId);
  if (roles.length === 0) return `No security roles are associated with model-driven app ${appId}.`;
  return [
    `Security roles for model-driven app ${appId} (${roles.length}):`,
    ...roles.map((role) => `  ${role.name ?? "Unnamed role"} (${role.roleid})`),
  ].join("\n");
}

export async function handleGrantModelDrivenAppRole(
  api: ModelDrivenApi,
  input: unknown,
): Promise<string> {
  const parsed = roleSchema.parse(input);
  await api.associateSecurityRole(parsed.appId, parsed.roleId);
  return `Granted security role ${parsed.roleId} access to model-driven app ${parsed.appId}.`;
}

export async function handleRevokeModelDrivenAppRole(
  api: ModelDrivenApi,
  input: unknown,
): Promise<string> {
  const parsed = roleSchema.parse(input);
  await api.disassociateSecurityRole(parsed.appId, parsed.roleId);
  return `Revoked security role ${parsed.roleId} access to model-driven app ${parsed.appId}.`;
}

export const modelDrivenTools: Tool[] = [
  listModelDrivenAppsTool,
  getModelDrivenAppTool,
  createModelDrivenAppTool,
  updateModelDrivenAppTool,
  deleteModelDrivenAppTool,
  getModelDrivenAppComponentsTool,
  addModelDrivenAppComponentsTool,
  removeModelDrivenAppComponentsTool,
  validateModelDrivenAppTool,
  publishModelDrivenAppTool,
  listModelDrivenAppRolesTool,
  grantModelDrivenAppRoleTool,
  revokeModelDrivenAppRoleTool,
];
